


import {
  defineComponent,
  computed,
  PropType,
  ref,
} from '@vue/composition-api';

import type { HokkaidoAirportReportExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportReport';
import { dtFormat } from '@/lib/dateHelper';
import { useRoute } from '@/hooks/useRoute';
import { buildTrackVisibilityQuery } from '@/lib/externalReports/externalReportHelper';
import { PaneStyle } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMap';
import {
  PANE_SIDE_HARD_MAX_WIDTH_PX,
  LIST_HARD_MAX_HEIGHT_PX,
} from '@/components/ExternalReports/HokkaidoAirport/consts';
import { useHokkaidoAirportReports } from '@/components/ExternalReports/HokkaidoAirport/composables/useReports';
import type {
  HokkaidoAirportSettings,
  HokkaidoAirportLabelPc,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';

export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<HokkaidoAirportSettings>,
      required: true,
    },
    isTrackVisible: {
      type: Boolean,
      required: true,
    },
    paneStyle: {
      type: Object as PropType<PaneStyle>,
      default: () => ({
        paneSideMinWidth: PANE_SIDE_HARD_MAX_WIDTH_PX,
        paneSideMaxWidth: PANE_SIDE_HARD_MAX_WIDTH_PX,
        listMinHeight: LIST_HARD_MAX_HEIGHT_PX,
        listMaxHeight: LIST_HARD_MAX_HEIGHT_PX,
      }),
    },
  },
  emits: [
    'click-report-on-report-list',
    'change-photo-type-visibility',
    'change-track-visibility',
    'show-photo-modal',
  ],
  setup(props, { emit }) {
    const { reportState, getSelectedReport } = useHokkaidoAirportReports();
    const photoTypes = computed<string[]>(() => {
      return [...props.userSettings.choices.photo_types];
    });
    const checkedPhotoTypes = ref<Record<string, boolean>>(photoTypes.value.reduce((acc, type) => {
      acc[type] = true;
      return acc;
    }, {} as Record<string, boolean>));
    const pcLabels = computed<HokkaidoAirportLabelPc>(() => (props.userSettings?.pc ?? {}) as HokkaidoAirportLabelPc);

    const baseArrivalTsDisp = (report: HokkaidoAirportReportExt): string => {
      const baseDepartureDate = dtFormat(report.baseDepartureTs, 'yyyy/mm/dd');
      const baseArrivalDate = dtFormat(report.baseArrivalTs, 'yyyy/mm/dd');
      if (baseDepartureDate === baseArrivalDate) {
        return dtFormat(report.baseArrivalTs, 'HH:MM');
      }
      return dtFormat(report.baseArrivalTs, 'yyyy/mm/dd HH:MM');
    };
    const onClickReportOnReportList = (report: HokkaidoAirportReportExt) => {
      emit('click-report-on-report-list', report);
    };
    const onChangePhotoTypeVisibility = (photoType: string) => {
      emit('change-photo-type-visibility', photoType, checkedPhotoTypes.value[photoType]);
    };
    const onChangeTrackVisibility = (): void => {
      emit('change-track-visibility', !props.isTrackVisible);
    };
    const { router } = useRoute();
    const onClickReportPrintBtn = (event: MouseEvent, hokkaidoAirportReportId: number) => {
      // 一覧の印刷ボタンを押した時に、報告書の選択イベントが発生しないようにする
      event.stopPropagation();
      const routeObj = {
        name: 'PrintExternalHokkaidoAirportReport',
        params: {
          externalReportId: hokkaidoAirportReportId.toString(),
        },
        query: buildTrackVisibilityQuery(props.isTrackVisible),
      };
      const obj = router.resolve(routeObj);
      window.open(obj.href, '_blank');
    };
    const getPhotoCountByType = (photoType: string): number => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) return 0;

      return selectedReport.photosExt.filter(photo => photo.photoType === photoType).length;
    };

    const showPhotosByType = (photoType: string) => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) {
        return;
      }
      const photos = selectedReport.photosExt.filter(photo => photo.photoType === photoType);
      emit('show-photo-modal', photoType, photos);
    };
    return {
      // refs
      reportState,
      photoTypes,
      pcLabels,
      checkedPhotoTypes,
      // methods
      baseArrivalTsDisp,
      onClickReportOnReportList,
      onChangePhotoTypeVisibility,
      onChangeTrackVisibility,
      onClickReportPrintBtn,
      getPhotoCountByType,
      showPhotosByType,
      dtFormat,
    };
  },
});
